// pages/TutorialDetail.js
import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import './TutorialDetail.css'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import NoteAddIcon from '@mui/icons-material/NoteAdd'
import FactCheckIcon from '@mui/icons-material/FactCheck'
import { tutorialsData } from '../tutorialsData'
import { useTutorialProgress } from '../hooks/useTutorialProgress'
import toast from 'react-hot-toast'

function TutorialDetail() {
  const { tutorialId } = useParams()
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState(0)
  const [showQuickJump, setShowQuickJump] = useState(false)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxImage, setLightboxImage] = useState(null)
  const [isAnimating, setIsAnimating] = useState(false)
  const contentRef = useRef(null)
  const { progress, saveProgress, markCompleted, isCompleted } = useTutorialProgress(tutorialId)

  // Find the tutorial
  const tutorial = tutorialsData.find(t => t.id === tutorialId)
  const tutorialIcons = {
    'wissen-hinzufuegen': <NoteAddIcon />,
    'wissen-moderieren-und-verwalten': <FactCheckIcon />
  }
  const tutorialIcon = tutorial ? (tutorialIcons[tutorial.id] || tutorial.icon) : null

  // Load saved progress on mount
  useEffect(() => {
    if (tutorial && progress.currentStep > 0 && !progress.completed) {
      setCurrentStep(progress.currentStep)
    }
  }, [tutorial, progress.currentStep, progress.completed])

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't handle keyboard shortcuts when typing in inputs or textareas
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return
      }

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault()
          if (currentStep > 0) {
            setCurrentStep(currentStep - 1)
          }
          break
        case 'ArrowRight':
          e.preventDefault()
          if (currentStep < tutorial.steps.length - 1) {
            setCurrentStep(currentStep + 1)
          }
          break
        case 'Escape':
          if (lightboxOpen) {
            setLightboxOpen(false)
          } else {
            navigate('/tutorials')
          }
          break
        default:
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [currentStep, lightboxOpen, navigate, tutorial.steps.length])

  // Scroll to top when step changes
  useEffect(() => {
    setIsAnimating(true)
    window.scrollTo({ top: 0, behavior: 'smooth' })
    
    // Save progress
    if (tutorial) {
      saveProgress(currentStep, tutorial.steps.length)
    }

    // Reset animation state
    const timer = setTimeout(() => setIsAnimating(false), 300)
    return () => clearTimeout(timer)
  }, [currentStep, tutorial, saveProgress])

  if (!tutorial) {
    return (
      <div className='tut-detail-page'>
        <div className='tut-detail-error'>
          <h2>Anleitung nicht gefunden</h2>
          <button onClick={() => navigate('/tutorials')} className='tut-back-btn'>
            Zurück zu Anleitungen
          </button>
        </div>
      </div>
    )
  }

  const step = tutorial.steps[currentStep]
  const isFirstStep = currentStep === 0
  const isLastStep = currentStep === tutorial.steps.length - 1

  const handleNext = () => {
    if (currentStep < tutorial.steps.length - 1) {
      setCurrentStep(currentStep + 1)
    }
  }

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleStepClick = (index) => {
    setCurrentStep(index)
  }

  const handleComplete = () => {
    markCompleted()
    toast.success('Tutorial abgeschlossen! 🎉', {
      duration: 3000,
      icon: '✅'
    })
    setTimeout(() => {
      navigate('/tutorials')
    }, 500)
  }

  const handleImageClick = (imageSrc) => {
    setLightboxImage(imageSrc)
    setLightboxOpen(true)
  }

  const closeLightbox = () => {
    setLightboxOpen(false)
    setLightboxImage(null)
  }

  return (
    <div className='tut-detail-page'>
      {/* Header */}
      <div className='tut-detail-header'>
        <button 
          onClick={() => navigate('/tutorials')} 
          className='tut-back-btn'
          aria-label='Zurück zu Anleitungen'
        >
          <ArrowBackIcon />
          Zurück
        </button>
        <div className='tut-detail-header-content'>
          <span className='tut-detail-icon'>{tutorialIcon}</span>
          <div>
            <h1 className='tut-detail-title'>{tutorial.title}</h1>
            <p className='tut-detail-subtitle'>{tutorial.description}</p>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className='tut-progress-section'>
        <div className='tut-progress-bar'>
          <div 
            className='tut-progress-fill'
            style={{ width: `${((currentStep + 1) / tutorial.steps.length) * 100}%` }}
          />
        </div>
        <div className='tut-progress-text'>
          Schritt {currentStep + 1} von {tutorial.steps.length}
          {isCompleted && (
            <span className='tut-completed-badge'>
              <CheckCircleIcon fontSize='small' />
              Abgeschlossen
            </span>
          )}
        </div>
      </div>

      {/* Step Navigation */}
      <div className='tut-step-nav'>
        <div className='tut-quick-jump'>
          <button
            className='tut-quick-jump-btn'
            onClick={() => setShowQuickJump(!showQuickJump)}
            aria-label='Schritt auswählen'
          >
            <span>Schritt {currentStep + 1}: {tutorial.steps[currentStep].title}</span>
            <ExpandMoreIcon className={showQuickJump ? 'rotated' : ''} />
          </button>
          {showQuickJump && (
            <div className='tut-quick-jump-menu'>
              {tutorial.steps.map((s, index) => (
                <button
                  key={s.id}
                  className={`tut-quick-jump-item ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
                  onClick={() => {
                    handleStepClick(index)
                    setShowQuickJump(false)
                  }}
                >
                  <span className='tut-quick-jump-number'>
                    {index < currentStep ? <CheckCircleIcon fontSize='small' /> : index + 1}
                  </span>
                  <span className='tut-quick-jump-title'>{s.title}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className='tut-step-indicators'>
          {tutorial.steps.map((s, index) => (
            <div
              key={s.id}
              className={`tut-step-indicator ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
              onClick={() => handleStepClick(index)}
              role='button'
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleStepClick(index)
                }
              }}
              aria-label={`Schritt ${index + 1}: ${s.title}`}
            >
              <div className='tut-step-number'>
                {index < currentStep ? <CheckCircleIcon fontSize='small' /> : index + 1}
              </div>
              <span className='tut-step-label'>{s.title}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div 
        className={`tut-step-content ${isAnimating ? 'animating' : ''}`}
        ref={contentRef}
      >
        <div className='tut-step-header'>
          <h2>{step.title}</h2>
          <p className='tut-step-description'>{step.description}</p>
        </div>

        {step.image && (
          <div 
            className='tut-step-image'
            onClick={() => handleImageClick(step.image)}
            role='button'
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                handleImageClick(step.image)
              }
            }}
            aria-label='Bild vergrößern'
          >
            <img 
              src={step.image} 
              alt={step.title}
              loading='lazy'
              onLoad={() => {
                console.log('[Tutorial Image] Successfully loaded:', {
                  imageSrc: step.image,
                  fullUrl: step.image.startsWith('http') ? step.image : `${window.location.origin}${step.image}`,
                  stepTitle: step.title
                })
              }}
              onError={(e) => {
                const attemptedUrl = e.target.src
                const fullUrl = attemptedUrl.startsWith('http') ? attemptedUrl : `${window.location.origin}${attemptedUrl}`
                
                console.error('[Tutorial Image] Failed to load:', {
                  imageSrc: step.image,
                  attemptedUrl,
                  fullUrl,
                  stepTitle: step.title,
                  windowLocation: window.location.href,
                  publicUrl: process.env.PUBLIC_URL || '(empty)'
                })
                
                e.target.style.display = 'none'
                if (e.target.parentElement) {
                  e.target.parentElement.innerHTML = `
                    <div class="tut-image-error">
                      <p>Bild konnte nicht geladen werden</p>
                      <p style="font-size: 0.75rem; color: #666; margin-top: 0.5rem;">
                        Versuchter Pfad: ${attemptedUrl}
                      </p>
                      <p style="font-size: 0.75rem; color: #666;">
                        Vollständige URL: ${fullUrl}
                      </p>
                    </div>
                  `
                }
              }}
            />
            <div className='tut-image-zoom-hint'>
              <span>Klicken zum Vergrößern</span>
            </div>
          </div>
        )}

        <div 
          className='tut-step-text'
          dangerouslySetInnerHTML={{ __html: step.content }}
        />
      </div>

      {/* Lightbox */}
      {lightboxOpen && lightboxImage && (
        <div className='tut-lightbox-overlay' onClick={closeLightbox}>
          <div className='tut-lightbox-content' onClick={(e) => e.stopPropagation()}>
            <button 
              className='tut-lightbox-close'
              onClick={closeLightbox}
              aria-label='Lightbox schließen'
            >
              ✕
            </button>
            <img src={lightboxImage} alt={step.title} />
          </div>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className='tut-nav-buttons'>
        <button
          onClick={handlePrevious}
          disabled={isFirstStep}
          className='tut-nav-btn tut-nav-btn-prev'
          aria-label='Vorheriger Schritt'
        >
          <NavigateBeforeIcon />
          Zurück
        </button>

        {isLastStep ? (
          <button
            onClick={handleComplete}
            className='tut-nav-btn tut-nav-btn-complete'
            aria-label='Tutorial abschließen'
          >
            <CheckCircleIcon />
            Abschließen
          </button>
        ) : (
          <button
            onClick={handleNext}
            className='tut-nav-btn tut-nav-btn-next'
            aria-label='Nächster Schritt'
          >
            Weiter
            <NavigateNextIcon />
          </button>
        )}
      </div>
    </div>
  )
}

export default TutorialDetail